import { Alert, Group, SimpleGrid, Stack, Text } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { AdminActionButton } from "./AdminActionButton";
import { FieldRow } from "./DetailGrid";
import { GenerationBadge, UpgradeOffersErrorState } from "./GenerationBadge";
import { RelatedLink } from "./RelatedLink";
import { StatusBadge } from "./StatusBadge";
import { Timestamp } from "./Timestamp";
import { notApplicable, offerWalletUpgrade, withdrawWalletUpgrade } from "@/api/actions";
import type { ReadState } from "@/firestore/hooks";
import type { WalletDoc, WalletUpgradeOfferDoc, WithDocId } from "@/firestore/types";
import { isPermissionError } from "@/lib/errors";
import { canOfferFixedGeneration } from "@/wallet/generations";
import { isLiveOffer, offerAvailability, withdrawAvailability } from "@/wallet/upgradeOffers";
import {
  MIGRATION_START_REFUSAL_HELP,
  MIGRATION_START_REFUSAL_LABELS,
} from "@/wallet/migrationCohorts";

/**
 * The Offer / Withdraw commands for one wallet's case-by-case upgrade (wallet-service 0.75.0).
 * Each button is keyed on the version it is fenced on (Offer: the wallet document; Withdraw: the
 * offer), so a document that moves while the dialog is open closes it.
 */
export function UpgradeOfferActions({
  wallet,
  generation,
  offer,
}: {
  wallet: WithDocId<WalletDoc>;
  generation: string | null;
  offer: ReadState<WithDocId<WalletUpgradeOfferDoc> | null>;
}) {
  const offerReadable = !offer.error;
  const current = offer.loading ? undefined : offer.data;
  const canOffer = offerAvailability({
    wallet,
    generation,
    offer: current,
    offerReadable,
  });
  const canWithdraw = withdrawAvailability(current);
  // Only wallets that could ever be offered show the Offer button; a live offer always shows Withdraw.
  const showOffer = canOfferFixedGeneration(generation) || generation === null;
  return (
    <Group gap="xs">
      {showOffer ? (
        <AdminActionButton
          key={`offer-${wallet.updatedAt}`}
          action={
            canOffer.offered ? offerWalletUpgrade : notApplicable(offerWalletUpgrade, canOffer.note)
          }
          params={{ walletId: wallet._docId, updatedAt: wallet.updatedAt }}
        />
      ) : null}
      {current && isLiveOffer(current) ? (
        <AdminActionButton
          key={`withdraw-${current.updatedAt}`}
          action={
            canWithdraw.offered
              ? withdrawWalletUpgrade
              : notApplicable(withdrawWalletUpgrade, canWithdraw.note)
          }
          params={{ walletId: wallet._docId, updatedAt: current.updatedAt }}
        />
      ) : null}
    </Group>
  );
}

/**
 * The wallet's upgrade offer (`walletUpgradeOffers/{walletId}`, admin-only): its state, target,
 * who offered or withdrew it and why, and the migration started under it. The owner sees only
 * `wallets/{id}.upgradeOffer` ("Upgrade available"), shown here as a cross-check.
 */
export function UpgradeOfferSummary({
  wallet,
  offer,
}: {
  wallet: WithDocId<WalletDoc>;
  offer: ReadState<WithDocId<WalletUpgradeOfferDoc> | null>;
}) {
  if (offer.error) {
    // An undeployed collection is the normal pre-0.75.0 state; anything else is shown as is.
    return isPermissionError(offer.error) ? (
      <Text size="sm" c="dimmed">
        Upgrade offers cannot be read in this environment yet (wallet-service 0.75.0).
      </Text>
    ) : (
      <UpgradeOffersErrorState error={offer.error} />
    );
  }
  if (offer.loading) return null;
  const o = offer.data;
  const ownerSees = !!wallet.upgradeOffer;
  if (!o) {
    return (
      <Text size="sm" c="dimmed">
        No upgrade offer. The owner cannot start a v3.8 migration until one is offered.
      </Text>
    );
  }
  return (
    <Stack gap="xs" data-testid="upgrade-offer">
      <Group gap="xs">
        <Text size="sm" fw={600}>
          Upgrade offer
        </Text>
        <StatusBadge state={o.state} />
        <GenerationBadge generation={o.fromGeneration} />
        <Text size="sm">→</Text>
        <GenerationBadge generation={o.toGeneration} />
      </Group>
      {o.state === "offered" && !ownerSees && wallet.upgradeOffer !== undefined ? (
        <Alert color="orange" variant="light" icon={<IconInfoCircle size={16} />} py={6}>
          The offer is live but the owner&apos;s wallet document carries no upgradeOffer yet: the
          app will not show &quot;Upgrade available&quot; until the projection catches up.
        </Alert>
      ) : null}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="sm">
        <FieldRow label="Offered by" value={<RelatedLink entity="user" id={o.offeredBy} />} />
        <FieldRow label="Offered at" value={<Timestamp value={o.offeredAt} absolute />} />
        <FieldRow
          label="Migration"
          value={<RelatedLink entity="walletMigration" id={o.migration} />}
        />
        <FieldRow label="Accepted at" value={<Timestamp value={o.acceptedAt} absolute />} />
        <FieldRow label="Consumed at" value={<Timestamp value={o.consumedAt} absolute />} />
        <FieldRow label="Withdrawn at" value={<Timestamp value={o.withdrawnAt} absolute />} />
        <FieldRow label="Withdrawn by" value={<RelatedLink entity="user" id={o.withdrawnBy} />} />
        <FieldRow
          label="Owner sees"
          value={ownerSees ? '"Upgrade available"' : "Nothing (no live offer to accept)"}
        />
      </SimpleGrid>
      <FieldRow label="Offer reason" value={o.offerReason || "—"} />
      {o.withdrawalReason ? (
        <FieldRow label="Withdrawal reason" value={o.withdrawalReason} />
      ) : null}
      {o.state === "offered" ? (
        // The cohort refusals an owner may report when the start fails (nothing is written).
        <Stack gap={2} data-testid="start-refusal-help">
          <Text size="xs" c="dimmed">
            If the owner reports a refused start:
          </Text>
          {Object.entries(MIGRATION_START_REFUSAL_HELP).map(([code, help]) => (
            <Text key={code} size="xs" c="dimmed">
              <Text span size="xs" fw={600}>
                {MIGRATION_START_REFUSAL_LABELS[code]} ({code})
              </Text>
              : {help}
            </Text>
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}
